import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useUserData } from "../contexts/UserContext";
import { PersonRecord } from "../types/data";
import { enableStickyWatcher } from "../utils/stickyWatcher";
import FadeInWhenVisible from "../components/FadeInWhenVisible";
import RespondentCard from "../cards/Respondent";
import RespondentDetailCard from "../cards/RespondentDetails";
import ModalWindow from "../components/ModalWindow";
import { convertNumberInCurrentLang } from "../utils/misc";
import PullToRefresh from "../components/PullToRefresh";

export default function PersonList() {
  const perPage = 100;
  const navigate = useNavigate();
  const { t, lang, setLang } = useLang();
  const { user, getSurveyedPersons, deletePerson } = useUserData();
  const [modalChildren, setModalChildren] = useState<React.ReactNode>(null);
  const [surveyedPersons, setSurveyedPersons] = useState<PersonRecord[]>([]);
  const [search, setSearch] = useState("");
  const [displayCount, setDisplayCount] = useState(perPage);

  useEffect(() => {
    enableStickyWatcher();
    
    // Scroll to person card if hash is present
    if (window.location.hash) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const element = document.querySelector(window.location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, []);

  useEffect(() => {
    loadPersons();
  }, []);

  // Reset display count when search changes
  useEffect(() => {
    setDisplayCount(perPage);
  }, [search]);

  const loadPersons = () => {
    let surveyed_persons = getSurveyedPersons();
    if (!surveyed_persons || surveyed_persons.length === 0) {
      setSurveyedPersons([]);
      return;
    }
    surveyed_persons = surveyed_persons.sort((a, b) => {
      return a.added_on && b.added_on
        ? new Date(b.added_on).getTime() - new Date(a.added_on).getTime()
        : 0;
    });

    setSurveyedPersons(surveyed_persons);
  };

  const handleRefresh = async () => {
    // Simulate a small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    loadPersons();
  };

  const loadMore = () => {
    setDisplayCount(prev => prev + perPage);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm("Are you sure you want to delete this person?")) return;
    const filtered = surveyedPersons.filter((p) => p.id !== id);
    if (filtered) {
      deletePerson(id);
      setSurveyedPersons(filtered);
      setModalChildren(null);
      window.alert(t("respondentDeleted"));
    }
  };

  const handlePersonClick = (id: string) => {
    //window.alert(`PersonList:: Clicked on ${id}`);
    const person = surveyedPersons.find((p) => p.id === id);
    if (!person) return;
    const modalChildren = (
      <div className="bg-white bg-opacity-75 backdrop-blur-md rounded-3xl shadow-lg mx-scr p-4">
        <div className="flex flex-col gap-4 ">
          <RespondentDetailCard person={person} />
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              className="grow button-xs bg-blue-500 text-white flex items-center justify-center"
              onClick={() => navigate(`/profile/${id}`)}
            >
              {t("editProfile")}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(person.id); }}
              className="grow button-xs button-bg-grad-red flex items-center justify-center"
            >
              <div className="flex flex-row gap-1 items-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>

                {t("delete")}
              </div>
            </button>
          </div>
        </div>
      </div>
    );
    setModalChildren(modalChildren);
    //window.alert(modalChildren)
  };

  const modalCloseHandler = () => {
    setModalChildren(null);
  };

  return (
    <PullToRefresh onRefresh={handleRefresh} enabled={false}>
      <div
        className={
          "page relative flex flex-col grow pt-12 bg-transparent text-center"
        }
      >
        <div
          className="sticky-header sticky z-10 mb-4 px-scr py-4"
          data-watch-sticky
          data-sticky-top="auto"
        >
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/survey')}
              className="p-1 rounded-full text-pink-600 bg-white/75 hover:bg-white transition-colors"
              aria-label={t("goBack")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="size-6" viewBox="0 0 24 24"><path fill="currentColor" d="M20 11.25a.75.75 0 0 1 0 1.5h-9.25V18a.75.75 0 0 1-1.28.53l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.28.53v5.25z" /></svg>
            </button>
            <h1><span className="text-pink-600">{t("surveyedPersonsList")}</span></h1>
            <div className="w-8"></div>
          </div>
        </div>

        <div className="px-scr pb-12 flex flex-col grow">
          <div className="flex justify-between items-center px-6 py-4 bg-slate-900 text-white bg-opacity-50 backdrop-blur-sm rounded-3xl shadow-sm">
            <h3 className="text-xl font-semibold">{t("yourSurveyedPersons")}</h3>
            <div className="flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-white text-blue-700 font-black">
              {convertNumberInCurrentLang(lang, surveyedPersons.length)}
            </div>
          </div>

          {/* Search field if more than 15 respondents */}
          {surveyedPersons.length >= 10 && (
            <div className="flex justify-center mt-4">
              <input
                type="text"
                className="input-base w-full rounded-xl border border-gray-300 px-4 !py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder={t("search") + "..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          )}

          <div className="flex-grow mt-4 pb-8">
            {surveyedPersons.length === 0 ? (
              <div className="py-6 flex flex-col items-center">
                <p className="px-6 text-gray-600">{t("noRespondents")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {surveyedPersons
                  .filter(person => {
                    if (!search) return true;
                    const q = search.toLowerCase();
                    return (
                      (person.name && person.name.toLowerCase().includes(q)) ||
                      (person.mobile && person.mobile.toLowerCase().includes(q)) ||
                      (person.notes && person.notes.toLowerCase().includes(q))
                    );
                  })
                  .slice(0, displayCount)
                  .map((person, i) => {
                  const lastLog = person.records?.[person.records.length - 1];
                  return (
                    <FadeInWhenVisible key={person.id} index={i} staggeredInitial>
                      <div id={`person-${person.id}`}>
                        <RespondentCard
                          person={person}
                          onDelete={(id) => handleDelete(id)}
                          onClick={(id) => handlePersonClick(id)}
                        />
                      </div>
                    </FadeInWhenVisible>
                  );
                })}
                
                {/* Load More button */}
                {surveyedPersons.filter(person => {
                  if (!search) return true;
                  const q = search.toLowerCase();
                  return (
                    (person.name && person.name.toLowerCase().includes(q)) ||
                    (person.mobile && person.mobile.toLowerCase().includes(q)) ||
                    (person.notes && person.notes.toLowerCase().includes(q))
                  );
                }).length > displayCount && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={loadMore}
                      className="button-base button-bg-grad-1 !py-3 px-8"
                    >
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                        </svg>
                        <span>{t("loadMore")}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bottom-nav-bar flex justify-center gap-2 w-full sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
          {/* <button
            onClick={() => navigate("/survey")}
            className="button-small button-bg-grad-2"
          >
            <div className="flex flex-row gap-1 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="3"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>
              <span>{t("back")}</span>
            </div>
          </button> */}

          <button
            onClick={() => navigate("/survey/new-person")}
            className="button-small button-bg-grad-1"
          >
            <div className="flex flex-row gap-1 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="3"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              <span>{t("addNewPersonShort")}</span>
            </div>
          </button>
        </div>

        {modalChildren && (
          <ModalWindow isOpen={!!modalChildren} onClose={modalCloseHandler}>
            {modalChildren}
          </ModalWindow>
        )}
      </div>
    </PullToRefresh>
  );
}
